import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth, authReady } from './config';
import { describeAuthError } from './authErrors';

export interface Session {
  user: User;
  /** The company this person belongs to. */
  orgId: string;
  appRole: 'rep' | 'manager' | 'admin';
  /**
   * §7 — the companies an admin may reach, their own included. One element for
   * everybody else, so callers never have to special-case the ordinary account.
   */
  adminOrgIds: string[];
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOutNow: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Every path out of this callback has to end in a state change. An
      // exception escaping an async listener is an unhandled rejection that
      // React never sees: the form stops spinning, nothing is shown, and the
      // sign-in button looks dead. Reporting the failure is the whole job here.
      try {
        if (!user) {
          if (!cancelled) { setSession(null); setLoading(false); }
          return;
        }
        // Multi-tenancy lives in custom claims (§7): orgId plus the application
        // role, which is distinct from the sales role on the user document.
        const token = await user.getIdTokenResult();
        const orgId = token.claims.orgId as string | undefined;
        const appRole = token.claims.role as Session['appRole'] | undefined;
        const reach = token.claims.adminOrgIds;
        const adminOrgIds = Array.isArray(reach)
          ? (reach as unknown[]).filter((o): o is string => typeof o === 'string')
          : [];
        if (cancelled) return;
        if (!orgId || !appRole) {
          // The common cause is an account created by hand in the Firebase
          // console: that makes a login, but claims are only ever written by
          // provisioning, so the account exists and belongs to no one. Naming
          // the address makes it obvious which one to provision.
          setError(
            `${user.email ?? 'This account'} signed in, but has no organization assigned. ` +
            'An account created directly in the Firebase console has no access until it ' +
            'is provisioned — run the provisioning script for it, or invite it from the ' +
            'People tab of an existing administrator account.'
          );
          setSession(null);
        } else {
          setError(null);
          setSession({
            user,
            orgId,
            appRole,
            // Their own company is always in reach, whatever the claim says.
            adminOrgIds: [...new Set([orgId, ...adminOrgIds])].sort(),
          });
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('could not establish the session', err);
        setError(describeAuthError(err));
        setSession(null);
        setLoading(false);
      }
    });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  const value = useMemo<AuthState>(() => ({
    session,
    loading,
    error,
    signIn: async (email, password) => {
      setError(null);
      try {
        // `authReady` is inside the try on purpose. It settles long before
        // anyone clicks, so a rejection here is instantaneous — the button
        // flashes and resets with nothing shown, which reads as a dead button
        // rather than as a failure. Anything that can throw belongs in here.
        await authReady;
        await signInWithEmailAndPassword(auth, email, password);
      } catch (err) {
        // Credential failures stay vague so they cannot be used to discover
        // which addresses hold accounts; configuration failures are named,
        // because the person typing cannot otherwise tell them apart from a
        // wrong password. describeAuthError draws that line.
        setError(describeAuthError(err));
        throw new Error('sign-in failed');
      }
    },
    signOutNow: async () => { await signOut(auth); },
  }), [session, loading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
